import { useEffect } from 'react'

import { useAssociateDevice } from '../hooks/useAssociateDevice'
import { useForm } from 'react-hook-form'

import { Box, Flex, GridItem } from '@chakra-ui/react'

import {
  Button,
  FormInputSelectControl,
  SkeletonModalBody,
} from '@/components/ui'
import { DeviceType } from '@/graphql/generated/schemas'

import { EditDeviceFieldsI } from '../types/types'
import { AssociatedDevice } from './AssociatedDevice'
import { EditDeviceModalSection } from './EditDeviceModalSection'
import { EditTailgateGridButton } from './EditTailgateGridButton'
import { SectionHeader } from './SectionHeader'
import { TwoColumnGrid } from './TwoColumnGrid'

interface AssociateDevicesIProps {
  deviceData: EditDeviceFieldsI
  isOtherSectionLoading: boolean
  onSave?: () => void
}

export interface AssociatedDevicesI {
  associateDevice: {
    label: string
    value: string
  }
}

export const AssociateDevices = ({
  deviceData,
  isOtherSectionLoading,
  onSave,
}: AssociateDevicesIProps) => {
  const deviceType = deviceData?.type
  const { control, watch, reset } = useForm<AssociatedDevicesI>({
    mode: 'onChange',
  })
  const {
    associatedDeviceOptions,
    data,
    selectedDevices,
    addSelectedDeviceOption,
    handleAssociation,
    removeSelectedDeviceOption,
    isDirty,
    isLoading,
  } = useAssociateDevice({
    deviceData,
  })

  const isDoor = deviceType === DeviceType.Door
  const isCamera = deviceType === DeviceType.Camera

  // For door: one connection covers all cameras — grab its ID
  const tailgateConnectionId = isDoor
    ? data?.device?.tailgateConns?.edges?.[0]?.node?.id
    : undefined

  // For camera: build a map of doorId -> connectionId so each door row
  // can find its own connection
  const doorConnectionMap: Record<string, string> = isCamera
    ? (data?.device?.tailgateConns?.edges ?? []).reduce((map, edge) => {
        const conn = edge?.node
        if (conn?.door?.id) {
          map[conn.door.id] = conn.id
        }
        return map
      }, {} as Record<string, string>)
    : {}
  const watchAssociateDevice = watch('associateDevice')

  useEffect(() => {
    if (watchAssociateDevice) {
      addSelectedDeviceOption(watchAssociateDevice)
      reset({ associateDevice: null })
    }
  }, [watchAssociateDevice])

  return (
    <Box>
      {isOtherSectionLoading ? (
        <SkeletonModalBody />
      ) : (
        <EditDeviceModalSection>
          <TwoColumnGrid>
            <GridItem>
              <SectionHeader
                subTitle={`Associate devices with this ${deviceType?.toLowerCase()} to include clips in related incidents.`}
                title='Associated Devices'
              />
            </GridItem>
            <GridItem justifySelf='end'>
              <Button
                isDisabled={!isDirty}
                isLoading={isLoading}
                loadingText='Saving Changes'
                onClick={async () => {
                  await handleAssociation()
                  onSave?.()
                }}
              >
                Save
              </Button>
            </GridItem>
          </TwoColumnGrid>
          <TwoColumnGrid>
            <GridItem colSpan={2} maxWidth={600}>
              <FormInputSelectControl
                control={control}
                controlShouldRenderValue={false}
                defaultValue={null}
                errorMessage='Select a device'
                id='associateDevice'
                options={associatedDeviceOptions}
                placeholder='Select Devices'
                rules={{ required: true }}
              />
            </GridItem>
          </TwoColumnGrid>
          <TwoColumnGrid>
            <GridItem colSpan={2} maxWidth={800}>
              <Box maxH='550px' mt='3' overflowY='auto'>
                {selectedDevices?.map((d) => (
                  <Box key={d.value} margin={2} maxH='250px' mb='10px'>
                    <Flex alignItems='center' gap='8px'>
                      <Box flex={1}>
                        <AssociatedDevice
                          name={d?.label}
                          onClick={() => removeSelectedDeviceOption(d)}
                        />
                      </Box>
                      {/* Only Doors & Cameras should have Tailgating configs */}
                      {isDoor && tailgateConnectionId && (
                        <EditTailgateGridButton
                          cameraId={d.value}
                          cameraName={d.label}
                          connectionId={tailgateConnectionId}
                          deviceType={d?.type}
                          doorId={deviceData.id}
                          doorName={deviceData.name}
                          onSaveCallback={onSave}
                        />
                      )}
                      {isCamera && doorConnectionMap[d.value] && (
                        <EditTailgateGridButton
                          cameraId={deviceData.id}
                          cameraName={deviceData.name}
                          connectionId={doorConnectionMap[d.value]}
                          deviceType={d?.type}
                          doorId={d.value}
                          doorName={d.label}
                          onSaveCallback={onSave}
                        />
                      )}
                    </Flex>
                  </Box>
                ))}
              </Box>
            </GridItem>
          </TwoColumnGrid>
        </EditDeviceModalSection>
      )}
    </Box>
  )
}
